import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  initializeLeadTracking,
  getStoredLeadSource,
  SOCIAL_PLATFORMS,
} from "../utils/leadTracking";
import axios from "../utils/axios";

/**
 * Component to handle social media campaign redirects
 * URL format: /campaign?utm_source=facebook&utm_campaign=black_friday&utm_medium=paid&campaign_id=123
 */
const CampaignHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [campaignInfo, setCampaignInfo] = useState(null);

  useEffect(() => {
    const processCampaign = async () => {
      // Initialize tracking to capture all URL parameters
      const trackingData = initializeLeadTracking();

      // Get campaign-specific parameters
      const campaignId =
        searchParams.get("campaign_id") || searchParams.get("cid");
      const utmSource = searchParams.get("utm_source");
      const utmCampaign = searchParams.get("utm_campaign");
      const redirectTo = searchParams.get("redirect") || "/register";

      // If we have a campaign ID, try to fetch campaign details
      if (campaignId) {
        try {
          const response = await axios.get(`/api/campaigns/${campaignId}`);
          setCampaignInfo(response.data.campaign || response.data);
        } catch (error) {
          console.warn("Failed to fetch campaign details:", error);
          // Don't treat this as a blocking error - continue with tracking
        }
      }

      // Add a small delay to ensure tracking data is stored
      setTimeout(() => {
        setIsProcessing(false);
        // Redirect to registration page with campaign context
        navigate(redirectTo, { replace: true });
      }, 1000);
    };

    processCampaign();
  }, [searchParams, navigate]);

  const getSocialIcon = (source) => {
    switch (source?.toLowerCase()) {
      case SOCIAL_PLATFORMS.FACEBOOK:
        return "📘";
      case SOCIAL_PLATFORMS.INSTAGRAM:
        return "📷";
      case SOCIAL_PLATFORMS.YOUTUBE:
        return "📺";
      case SOCIAL_PLATFORMS.LINKEDIN:
        return "💼";
      case SOCIAL_PLATFORMS.TWITTER:
        return "🐦";
      default:
        return "🚀";
    }
  };

  const getSourceDisplayName = (source) => {
    switch (source?.toLowerCase()) {
      case SOCIAL_PLATFORMS.FACEBOOK:
        return "Facebook";
      case SOCIAL_PLATFORMS.INSTAGRAM:
        return "Instagram";
      case SOCIAL_PLATFORMS.YOUTUBE:
        return "YouTube";
      case SOCIAL_PLATFORMS.LINKEDIN:
        return "LinkedIn";
      case SOCIAL_PLATFORMS.TWITTER:
        return "Twitter";
      default:
        return "Social Media";
    }
  };

  if (isProcessing) {
    const trackingData = getStoredLeadSource();
    const utmSource = searchParams.get("utm_source");
    const utmCampaign = searchParams.get("utm_campaign");

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="text-6xl mb-4">{getSocialIcon(utmSource)}</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome from {getSourceDisplayName(utmSource)}!
            </h2>
            {utmCampaign && (
              <p className="text-gray-600">
                {utmCampaign
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}{" "}
                Campaign
              </p>
            )}
            {campaignInfo && (
              <p className="text-sm text-blue-600 mt-2">{campaignInfo.name}</p>
            )}
          </div>

          <div className="flex items-center justify-center space-x-2 text-gray-500">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span>Preparing your exclusive offer...</span>
          </div>

          <div className="mt-6 space-y-2 text-sm text-gray-600">
            <div className="flex items-center justify-center space-x-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span>Tracking campaign source</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span>Applying special offers</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
              <span>Redirecting to registration...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CampaignHandler;
